import React, { useEffect, useState } from 'react'
import { getQualification, patchQualification, updateDeal, type Qualification, type QualificationStatus } from './crmApi'
import { crmToast, Overlay } from './crmUi'
import { SkeletonRows } from '../shared/Skeleton'

/**
 * Lead Qualification panel (PRD v1.0 — Fi, 6 Aug 2026). Replaces the old coarse
 * Qual checklist. Three pillars (ICP / BANT / Engagement) drive six must-have
 * gates; the STATUS IS SYSTEM-COMPUTED on the server — this UI only edits gate
 * fields and renders the result. Nurture/Disqualify are explicit actions that
 * demand a reason code; leaving Qualified any other way also demands one (the
 * server 400s and this panel then asks for the reason and retries).
 */

export const QUAL_STATUS_STYLE: Record<QualificationStatus, { label: string; bg: string; fg: string }> = {
  not_qualified: { label: 'Not Qualified', bg: '#F1F5F9', fg: '#475569' },
  working: { label: 'Working', bg: '#FEF3C7', fg: '#92400E' },
  qualified: { label: 'Qualified', bg: '#DCFCE7', fg: '#166534' },
  disqualified: { label: 'Disqualified', bg: '#FEE2E2', fg: '#991B1B' },
  nurture: { label: 'Nurture', bg: '#E0E7FF', fg: '#3730A3' },
}

/** Fi's PRD (18 Sep 2026): the 0–100 gate score with its colour band. */
export const QualPctBadge: React.FC<{ pct?: number | null; className?: string }> = ({ pct, className = '' }) => {
  if (pct === null || pct === undefined) return null
  const band = pct === 100
    ? { label: 'Qualified', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' }
    : pct >= 67
      ? { label: 'Nearly qualified', cls: 'bg-blue-50 text-blue-700 border-blue-300' }
      : pct >= 34
        ? { label: 'In progress', cls: 'bg-amber-50 text-amber-700 border-amber-300' }
        : { label: 'Early', cls: 'bg-red-50 text-red-700 border-red-300' }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tabular-nums border ${band.cls} ${className}`}
      title={`Qualification score: ${pct}% (gates passed ÷ 6) — ${band.label}`}>
      {pct}% <span className="font-semibold">{band.label}</span>
    </span>
  )
}

export const QualStatusChip: React.FC<{ status?: string; className?: string }> = ({ status, className }) => {
  const s = QUAL_STATUS_STYLE[(status as QualificationStatus) || 'not_qualified'] || QUAL_STATUS_STYLE.not_qualified
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${className || ''}`} style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  )
}

const GATE_LABELS: { key: keyof Qualification['gates']; label: string }[] = [
  { key: 'icp', label: 'ICP fit' },
  { key: 'need', label: 'Need' },
  { key: 'authority', label: 'Authority' },
  { key: 'budget', label: 'Budget' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'engagement', label: 'Engagement' },
]

const GateDots: React.FC<{ q: Qualification; size?: 'sm' | 'md' }> = ({ q, size = 'md' }) => (
  <div className={`flex flex-wrap ${size === 'sm' ? 'gap-1' : 'gap-1.5'}`}>
    {GATE_LABELS.map(({ key, label }) => {
      const g = q.gates[key]
      const color = g.pass ? '#10B981' : g.failed ? '#EF4444' : '#CBD5E1'
      const hint = g.pass ? 'passes' : g.failed ? 'FAILS' : g.missing.length ? `missing: ${g.missing.join(', ')}` : 'incomplete'
      return (
        <span key={key} className={`inline-flex items-center gap-1 rounded-full border ${size === 'sm' ? 'px-1.5 py-px text-[9px]' : 'px-2 py-0.5 text-[10px]'} font-semibold`} style={{ borderColor: color, color: g.pass ? '#065F46' : g.failed ? '#991B1B' : '#64748B' }} title={`${label}: ${hint}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          {label}
        </span>
      )
    })}
  </div>
)

/* ── Small input building blocks ─────────────────────────────────────────── */

const Row: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <div className="min-w-0">
      <p className="text-[12px] font-medium text-txt-primary">{label}</p>
      {hint && <p className="text-[10px] text-txt-tertiary">{hint}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
)

const Select: React.FC<{ value: string | null; options: { value: string; label: string }[]; onChange: (v: string | null) => void }> = ({ value, options, onChange }) => (
  <select
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value || null)}
    className="rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400 max-w-[220px]"
  >
    <option value="">— not set —</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
)

const TriState: React.FC<{ value: boolean | null; onChange: (v: boolean | null) => void; yes?: string; no?: string }> = ({ value, onChange, yes = 'Yes', no = 'No' }) => (
  <div className="flex rounded-lg border border-border overflow-hidden">
    {[{ v: true, l: yes }, { v: false, l: no }, { v: null, l: '?' }].map(({ v, l }) => (
      <button
        key={String(v)}
        onClick={() => onChange(v)}
        className={`px-2.5 py-1 text-[11px] font-semibold ${value === v ? (v === true ? 'bg-emerald-600 text-white' : v === false ? 'bg-red-600 text-white' : 'bg-slate-500 text-white') : 'bg-surface text-txt-secondary hover:bg-surface-hover'}`}
      >
        {l}
      </button>
    ))}
  </div>
)

const Pillar: React.FC<{ title: string; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <div className="rounded-xl border border-border bg-surface p-3.5">
    <p className="text-[11px] font-bold uppercase tracking-wider text-txt-primary">{title}</p>
    <p className="text-[10px] text-txt-tertiary mb-1.5">{sub}</p>
    <div className="divide-y divide-border">{children}</div>
  </div>
)

/* ── The full panel ──────────────────────────────────────────────────────── */

export const DealQualification: React.FC<{ dealId: string; onChanged?: (q: Qualification) => void }> = ({ dealId, onChanged }) => {
  const [q, setQ] = useState<Qualification | null>(null)
  const [busy, setBusy] = useState(false)
  const [reasonAsk, setReasonAsk] = useState<{ pending: { fields?: Record<string, any>; action?: 'nurture' | 'disqualify' }; title: string } | null>(null)
  const [reason, setReason] = useState('')

  useEffect(() => { getQualification(dealId).then(setQ).catch(() => crmToast('Could not load qualification', 'error')) }, [dealId])

  const apply = (body: { fields?: Record<string, any>; action?: 'nurture' | 'disqualify' | 'reopen'; reason?: string }, title = 'This change') => {
    setBusy(true)
    patchQualification(dealId, body)
      .then((next) => { setQ(next); onChanged?.(next) })
      .catch((e: any) => {
        const msg = String(e?.message || '')
        if (/reason code is required/i.test(msg) && body.action !== 'reopen') {
          // Server guard rail: this edit moves the lead out of Qualified — collect the reason, retry.
          setReason('')
          setReasonAsk({ pending: body as any, title })
        } else {
          crmToast(msg || 'Could not save', 'error')
        }
      })
      .finally(() => setBusy(false))
  }
  const setField = (key: string, value: any) => apply({ fields: { [key]: value } })

  if (!q) return <SkeletonRows rows={5} rowClass="h-10" />
  const f = q.fields
  const th = q.options.thresholds
  const fmtGBP = (n: number) => `£${Number(n).toLocaleString()}`

  return (
    <div className="space-y-3" style={busy ? { opacity: 0.7, pointerEvents: 'none' } : undefined}>
      {/* Unqualified-demo banner REMOVED (Lee, 28 Oct 2026). */}
      {/* Status header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <QualStatusChip status={q.status} />
          <QualPctBadge pct={q.qualPct} />
          {q.autofill && Object.keys(q.autofill).filter((k) => k !== 'authoritySuggested').length > 0 && (
            <span className="text-[10px] text-txt-tertiary" title={Object.entries(q.autofill).filter(([k]) => k !== 'authoritySuggested').map(([k, v]) => `${k}: ${v.src}`).join(' · ')}>
              {Object.keys(q.autofill).filter((k) => k !== 'authoritySuggested').length} field(s) auto-filled — edit any to take it over
            </span>
          )}
          {q.autofill?.authoritySuggested && !q.fields.authorityStatus && (
            <span className="text-[10px] text-brand-600" title={q.autofill.authoritySuggested.src}>Authority suggestion: buyer engaged ({q.autofill.authoritySuggested.src})</span>
          )}
          {q.leadGrade && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 text-white" title={`Priority grade (score ${q.leadScore ?? '—'}) — never affects the in/out gate`}>Grade {q.leadGrade}</span>}
          {q.qualifiedAt && <span className="text-[10px] text-txt-tertiary">qualified {new Date(q.qualifiedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}{q.qualifiedBy ? ` · ${q.qualifiedBy}` : ''}</span>}
          {q.disqualifyReason && q.status !== 'qualified' && <span className="text-[10px] text-txt-tertiary">reason: {(q.options.disqualifyReasons.find((r) => r.value === q.disqualifyReason) || { label: q.disqualifyReason }).label}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          {q.hold ? (
            <button onClick={() => apply({ action: 'reopen' })} className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-border text-txt-primary hover:bg-surface-hover">Reopen</button>
          ) : (
            <>
              <button onClick={() => { setReason(''); setReasonAsk({ pending: { action: 'nurture' }, title: 'Move to Nurture' }) }} className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-border text-indigo-700 hover:bg-surface-hover">Nurture</button>
              <button onClick={() => { setReason(''); setReasonAsk({ pending: { action: 'disqualify' }, title: 'Disqualify' }) }} className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-border text-red-700 hover:bg-surface-hover">Disqualify</button>
            </>
          )}
        </div>
      </div>
      <GateDots q={q} />
      <p className="text-[10px] text-txt-tertiary">The status is computed by the server from the six gates — the grade only ranks qualified leads and never moves a lead in or out of the pipeline.</p>

      {/* Pillar A */}
      <Pillar title="Pillar A — ICP fit" sub="Are they the right kind of company? All four must match; a wrong answer hard-disqualifies.">
        <Row label="Target vertical" hint="Pro AV / residential AV"><Select value={f.icpVertical} options={q.options.verticals} onChange={(v) => setField('icpVertical', v)} /></Row>
        <Row label="Company size" hint={`${th.minCompanySize}+ staff to pass`}>
          <input
            type="number" min={0} defaultValue={f.companySize ?? ''} placeholder="staff"
            onBlur={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); if (v !== f.companySize) setField('companySize', v) }}
            className="w-24 rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400"
          />
        </Row>
        <Row label="Geography / market" hint="Sanctioned markets fail (rep attestation)"><Select value={f.geography} options={q.options.geographies} onChange={(v) => setField('geography', v)} /></Row>
        <Row label="Use-case fit" hint="Quote-to-completion workflow we address"><TriState value={f.useCaseFit} onChange={(v) => setField('useCaseFit', v)} /></Row>
      </Pillar>

      {/* Pillar B */}
      <Pillar title="Pillar B — BANT" sub="Is this a real, funded, decided-upon purchase? Stated by the prospect, not assumed.">
        <Row label="Need confirmed" hint="Specific pain we solve, stated by them"><TriState value={f.needConfirmed} onChange={(v) => setField('needConfirmed', v)} /></Row>
        <div className="py-1.5">
          <textarea
            defaultValue={f.needDetail ?? ''} rows={2} placeholder="The stated pain — ideally with £/time impact…"
            onBlur={(e) => { const v = e.target.value.trim() || null; if (v !== (f.needDetail || null)) setField('needDetail', v) }}
            className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-[12px] text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400 resize-none"
          />
        </div>
        <Row label="Authority" hint="Buyer engaged, or champion with access"><Select value={f.authorityStatus} options={q.options.authority} onChange={(v) => setField('authorityStatus', v)} /></Row>
        <Row label="Budget status" hint="Confirmed or realistic passes"><Select value={f.budgetStatus} options={q.options.budget} onChange={(v) => setField('budgetStatus', v)} /></Row>
        <Row label="Monthly quote volume" hint={`Must be over ${fmtGBP(th.minQuoteVolume)}/month (Pro tier)`}>
          <input
            type="number" min={0} defaultValue={f.monthlyQuoteVolume ?? ''} placeholder="£/month"
            onBlur={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); if (v !== f.monthlyQuoteVolume) setField('monthlyQuoteVolume', v) }}
            className="w-28 rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400"
          />
        </Row>
        <Row label="Estimated deal value" hint={`£/year ARR — gate needs ≥ ${fmtGBP(th.minDeal)}. This IS the deal's Value.`}>
          <div className="flex items-center gap-1.5">
            <input
              key={`edv-${f.estDealValue}`}
              type="number" min={0} defaultValue={f.estDealValue || ''} placeholder="£ ARR"
              onBlur={(e) => {
                const v = Number(e.target.value) || 0
                if (v === f.estDealValue) return
                setBusy(true)
                // Writes deals.amount — the server recomputes the gate on amount changes.
                updateDeal(dealId, { amount: v })
                  .then(() => getQualification(dealId))
                  .then((next) => { setQ(next); onChanged?.(next) })
                  .catch(() => crmToast('Could not save the deal value', 'error'))
                  .finally(() => setBusy(false))
              }}
              className="w-28 rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400"
            />
            <span className={`text-[11px] font-bold ${f.estDealValue >= th.minDeal ? 'text-emerald-600' : 'text-red-600'}`}>{f.estDealValue >= th.minDeal ? '✓' : `< ${fmtGBP(th.minDeal)}`}</span>
          </div>
        </Row>
        <Row label="Decision timeline" hint="Within 6 months passes"><Select value={f.decisionTimeline} options={q.options.timeline} onChange={(v) => setField('decisionTimeline', v)} /></Row>
      </Pillar>

      {/* Pillar C */}
      <Pillar title="Pillar C — Engagement" sub="Ticked automatically when a logged call/meeting completes; can be set by hand too.">
        <Row label="Two-way engagement" hint="They responded — not just emailed at"><TriState value={f.engagedTwoWay} onChange={(v) => setField('engagedTwoWay', v)} /></Row>
        <Row label="Discovery call held" hint="At least one live interaction"><TriState value={f.discoveryCallDone} onChange={(v) => setField('discoveryCallDone', v)} /></Row>
      </Pillar>

      {/* Reason-code dialog (nurture / disqualify / demoting edits) */}
      {reasonAsk && (
        <Overlay onClose={() => setReasonAsk(null)} width={380}>
          <div className="p-5">
            <p className="text-sm font-bold text-txt-primary mb-1">{reasonAsk.title}</p>
            <p className="text-[11px] text-txt-secondary mb-3">A reason code is required for reporting (PRD §7).</p>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[12px] text-txt-primary focus:outline-none mb-3">
              <option value="">— choose a reason —</option>
              {q.options.disqualifyReasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setReasonAsk(null)} className="px-3 py-1.5 rounded-lg text-xs border border-border text-txt-secondary hover:bg-surface-hover">Cancel</button>
              <button
                disabled={!reason}
                onClick={() => { const p = reasonAsk.pending; setReasonAsk(null); apply({ ...p, reason }) }}
                className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  )
}

/* ── Completion + modal wrappers (queue forced-fill + leads-page button) ──── */

/**
 * "Filled in" = every gate question answered (an honest No counts; 'unknown' on
 * authority/budget does NOT — that's a question you didn't ask), or an explicit
 * Nurture/Disqualify. Engagement is exempt — the spoke outcome ticks it.
 */
export const isQualificationComplete = (q: Qualification): boolean => {
  if (q.hold || q.status === 'qualified' || q.status === 'disqualified') return true
  const f = q.fields
  const answered = (v: any) => v !== null && v !== undefined
  return (
    answered(f.icpVertical) && answered(f.companySize) && answered(f.geography) && answered(f.useCaseFit) &&
    answered(f.needConfirmed) &&
    answered(f.authorityStatus) && f.authorityStatus !== 'unknown' &&
    answered(f.budgetStatus) && f.budgetStatus !== 'unknown' &&
    answered(f.monthlyQuoteVolume) && answered(f.decisionTimeline)
  )
}

const missingCount = (q: Qualification): number => {
  const f = q.fields
  const missing = [
    f.icpVertical, f.companySize, f.geography, f.useCaseFit, f.needConfirmed,
    f.authorityStatus === 'unknown' ? null : f.authorityStatus,
    f.budgetStatus === 'unknown' ? null : f.budgetStatus,
    f.monthlyQuoteVolume, f.decisionTimeline,
  ].filter((v) => v === null || v === undefined).length
  return missing
}

/** Dismissable qualification panel in an overlay — the Leads-page Qualify button. */
export const QualPanelOverlay: React.FC<{ dealId: string; dealName?: string; onClose: () => void }> = ({ dealId, dealName, onClose }) => (
  <Overlay onClose={onClose} width={580}>
    <div className="p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-txt-primary">Qualify{dealName ? ` — ${dealName}` : ''}</p>
        <button onClick={onClose} className="text-txt-tertiary hover:text-txt-primary text-lg leading-none px-1">×</button>
      </div>
      <DealQualification dealId={dealId} />
    </div>
  </Overlay>
)

/**
 * FORCED post-call qualification (Lee, 7 Aug 2026): after a spoke/booked outcome on
 * an Unqualified-queue entry the caller must fill the lead's answers — the modal
 * cannot be dismissed until every gate question is answered or the lead is
 * explicitly Nurtured/Disqualified.
 */
export const ForcedQualificationModal: React.FC<{ dealId: string; dealName?: string; onDone: () => void }> = ({ dealId, dealName, onDone }) => {
  const [q, setQ] = useState<Qualification | null>(null)
  useEffect(() => { getQualification(dealId).then(setQ).catch(() => {}) }, [dealId])
  const complete = q ? isQualificationComplete(q) : false
  const left = q ? missingCount(q) : 0
  const tryClose = () => { if (complete) onDone() }
  return (
    <Overlay onClose={tryClose} width={580}>
      <div className="p-4 overflow-y-auto">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-sm font-bold text-txt-primary">You just spoke to {dealName || 'this lead'} — qualify them now</p>
        </div>
        <p className={`text-[11px] mb-2 ${complete ? 'text-emerald-700' : 'text-amber-700'}`}>
          {complete
            ? 'All answered — you can close.'
            : `${left} answer${left === 1 ? '' : 's'} left ('unknown' doesn't count) — or Nurture / Disqualify with a reason. The call isn't finished until this is.`}
        </p>
        <DealQualification dealId={dealId} onChanged={setQ} />
        <div className="flex justify-end mt-3">
          <button
            onClick={tryClose}
            disabled={!complete}
            className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-40"
            title={complete ? 'Done' : 'Answer the remaining questions first'}
          >
            {complete ? 'Done — close' : `${left} left…`}
          </button>
        </div>
      </div>
    </Overlay>
  )
}

/* ── "What's happening" — the current lead summary (Lee, 18 Aug 2026) ──────── */

/** Derived server-side from facts (contact, meetings, WhatsApp, qualification,
 *  blocker, set-up stage) — always current, never stale. */
export const LeadSummaryCard: React.FC<{ dealId: string }> = ({ dealId }) => {
  const [summary, setSummary] = useState<string | null>(null)
  useEffect(() => {
    let dead = false
    import('./crmApi').then((m) => m.getLeadSummary(dealId)).then((r) => { if (!dead) setSummary(r.summary) }).catch(() => {})
    return () => { dead = true }
  }, [dealId])
  if (!summary) return null
  return (
    <div className="rounded-lg border border-border bg-surface-secondary/50 px-2.5 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary mb-1">What's happening</p>
      <p className="text-[11px] text-txt-secondary leading-relaxed">{summary}</p>
    </div>
  )
}

/* ── Gate 4 blocker card (funnel spec, 17 Aug 2026) — lead pop-up sidebar ──── */

const BLOCKER_OPTIONS: { value: string; label: string }[] = [
  { value: 'price', label: 'Price / budget' },
  { value: 'missing_feature', label: 'Missing feature' },
  { value: 'integration', label: 'Integration needed' },
  { value: 'data_migration', label: 'Data migration effort' },
  { value: 'time_bandwidth', label: 'No time / bandwidth' },
  { value: 'needs_approval', label: 'Needs internal approval' },
  { value: 'comparing', label: 'Comparing competitors' },
  { value: 'no_blocker', label: 'No blocker — confirmed' },
  { value: 'other', label: 'Other (see note)' },
]

/** "A named blocker to going paid, or 'no blocker' explicitly confirmed" — due
 *  at trial midpoint, escalated at expiry − 3 days. Saved instantly. */
export const BlockerCard: React.FC<{ dealId: string; initialBlocker?: string | null; initialNote?: string | null }> = ({ dealId, initialBlocker, initialNote }) => {
  const [blocker, setBlocker] = useState<string>(initialBlocker || '')
  const [note, setNote] = useState<string>(initialNote || '')
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const save = (b: string, n: string) => {
    if (!b) return
    import('./crmApi').then((m) => m.setDealBlocker(dealId, b, n.trim() || null))
      .then(() => { setSavedAt(new Date().toISOString()); crmToast('Blocker review saved', 'success') })
      .catch(() => crmToast('Could not save the blocker', 'error'))
  }
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary mb-1.5">Blocker to going paid</p>
      <select
        value={blocker}
        onChange={(e) => { setBlocker(e.target.value); save(e.target.value, note) }}
        className="w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400"
      >
        <option value="">— not reviewed yet —</option>
        {BLOCKER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => blocker && save(blocker, note)}
        placeholder="Detail (optional) — what exactly, and what would unblock it"
        rows={2}
        className="w-full mt-1.5 rounded-lg border border-border bg-surface px-2 py-1.5 text-[11px] text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400 resize-none"
      />
      {savedAt && <p className="text-[10px] text-emerald-600 mt-0.5">✓ reviewed just now</p>}
    </div>
  )
}

/* ── Compact sidebar card for the lead pop-up (replaces the old checklist) ── */

export const QualSidebarCard: React.FC<{ dealId: string; onStatusChange?: (status: string) => void }> = ({ dealId, onStatusChange }) => {
  const [q, setQ] = useState<Qualification | null>(null)
  const [open, setOpen] = useState(false)
  const load = () => getQualification(dealId).then(setQ).catch(() => {})
  useEffect(() => { load() }, [dealId]) // eslint-disable-line react-hooks/exhaustive-deps

  const passCount = q ? GATE_LABELS.filter(({ key }) => q.gates[key].pass).length : 0
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Qualification</p>
        {q && <QualStatusChip status={q.status} />}
      </div>
      {q ? (
        <>
          <GateDots q={q} size="sm" />
          <p className="text-[10px] text-txt-tertiary mt-1.5">{passCount}/6 gates pass</p>
        </>
      ) : (
        <p className="text-[10px] text-txt-tertiary">Loading…</p>
      )}
      <button onClick={() => setOpen(true)} className="mt-2 w-full text-[11px] font-semibold py-1.5 rounded-lg border border-border text-txt-primary hover:bg-surface-hover">
        Open qualification panel
      </button>
      {open && (
        <Overlay onClose={() => { setOpen(false); load() }} width={580}>
          <div className="p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-txt-primary">Lead qualification</p>
              <button onClick={() => { setOpen(false); load() }} className="text-txt-tertiary hover:text-txt-primary text-lg leading-none px-1">×</button>
            </div>
            <DealQualification dealId={dealId} onChanged={(next) => { setQ(next); onStatusChange?.(next.status) }} />
          </div>
        </Overlay>
      )}
    </div>
  )
}
